import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, Phone } from 'lucide-react';
import { COUNTRIES_WITH_PHONE, CountryPhoneInfo, resolveCountryPhoneInfo, getPhonePlaceholderForCountry } from '../../lib/countryPhoneData';

interface PhoneCountryInputProps {
  value: string;
  onChange: (fullPhone: string) => void;
  selectedCountry: string;
  onCountryChange?: (countryName: string) => void;
  disabled?: boolean;
  hasError?: boolean;
}

export const PhoneCountryInput: React.FC<PhoneCountryInputProps> = ({
  value,
  onChange,
  selectedCountry,
  onCountryChange,
  disabled = false,
  hasError = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountryInfo, setSelectedCountryInfo] = useState<CountryPhoneInfo>(() =>
    resolveCountryPhoneInfo(selectedCountry)
  );
  const [localNumber, setLocalNumber] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Sync country code when selectedCountry prop changes
  useEffect(() => {
    const info = resolveCountryPhoneInfo(selectedCountry);
    setSelectedCountryInfo(info);
  }, [selectedCountry]);

  // Parse initial or incoming value
  useEffect(() => {
    if (!value) {
      setLocalNumber('');
      return;
    }
    // If value starts with +, check if it matches a dial code
    if (value.startsWith('+')) {
      const match = COUNTRIES_WITH_PHONE.find((c) => value.startsWith(c.dialCode));
      if (match) {
        setSelectedCountryInfo(match);
        const rest = value.slice(match.dialCode.length).trim();
        setLocalNumber(rest);
        return;
      }
    }
    setLocalNumber(value.trim());
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  const handleSelectCountry = (country: CountryPhoneInfo) => {
    setSelectedCountryInfo(country);
    setIsOpen(false);
    setSearchQuery('');
    if (onCountryChange) {
      onCountryChange(country.name);
    }
    // Construct new full phone
    const full = localNumber ? `${country.dialCode} ${localNumber}`.trim() : country.dialCode;
    onChange(full);
  };

  const handleLocalNumberChange = (raw: string) => {
    setLocalNumber(raw);
    const cleaned = raw.trim();
    if (!cleaned) {
      onChange('');
    } else {
      onChange(`${selectedCountryInfo.dialCode} ${cleaned}`.trim());
    }
  };

  const filteredCountries = COUNTRIES_WITH_PHONE.filter((c) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return (
      c.name.toLowerCase().includes(q) ||
      c.dialCode.includes(q) ||
      c.iso2.includes(q)
    );
  });

  return (
    <div className="relative" ref={containerRef}>
      <div className={`flex items-center w-full rounded-xl bg-slate-900 border transition-colors overflow-hidden ${
        hasError ? 'border-red-500/80 focus-within:border-red-400' : 'border-slate-700 focus-within:border-cyan-400'
      }`}>
        {/* Country Code Trigger Button */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 px-3 py-2.5 bg-slate-800/80 hover:bg-slate-800 text-xs font-bold text-slate-200 border-r border-slate-700/80 shrink-0 transition-colors cursor-pointer select-none"
          title={`Country Code: ${selectedCountryInfo.name} (${selectedCountryInfo.dialCode})`}
        >
          <span className="text-base leading-none">{selectedCountryInfo.flag}</span>
          <span className="font-mono text-cyan-300 font-bold">{selectedCountryInfo.dialCode}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen ? 'rotate-180 text-cyan-400' : ''}`} />
        </button>

        {/* Local Phone Input */}
        <div className="relative flex-1 flex items-center">
          <input
            type="tel"
            disabled={disabled}
            value={localNumber}
            onChange={(e) => handleLocalNumberChange(e.target.value)}
            placeholder={getPhonePlaceholderForCountry(selectedCountryInfo.name)}
            className="w-full px-3 py-2.5 bg-transparent text-xs font-bold text-white placeholder:text-slate-600 focus:outline-none"
            required
          />
        </div>
      </div>

      {/* Country Code Selection Dropdown Popover */}
      {isOpen && (
        <div className="absolute left-0 top-full mt-1.5 w-72 sm:w-80 max-h-72 bg-slate-900/98 backdrop-blur-xl border border-cyan-500/40 rounded-2xl shadow-2xl z-[100] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
          {/* Search Header */}
          <div className="p-2 border-b border-white/10 bg-slate-950/80 flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400 shrink-0 ml-1" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search country or code (e.g. +880, US)..."
              className="w-full bg-transparent text-xs font-medium text-white placeholder:text-slate-500 focus:outline-none py-1"
            />
          </div>

          {/* Country List */}
          <div className="overflow-y-auto max-h-60 p-1.5 space-y-0.5 divide-y divide-white/5 scrollbar-thin scrollbar-thumb-slate-700">
            {filteredCountries.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-400">
                No matching country found
              </div>
            ) : (
              filteredCountries.map((c) => {
                const isSelected = selectedCountryInfo.iso2 === c.iso2;
                return (
                  <button
                    key={`${c.iso2}-${c.dialCode}`}
                    type="button"
                    onClick={() => handleSelectCountry(c)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-left text-xs transition-colors cursor-pointer ${
                      isSelected
                        ? 'bg-cyan-500/20 text-cyan-200 font-bold'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <span className="text-base shrink-0">{c.flag}</span>
                      <span className="truncate">{c.name}</span>
                    </div>
                    <span className="font-mono text-cyan-400 font-bold text-[11px] shrink-0 ml-2">
                      {c.dialCode}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
